export type BalanceCheckKey =
  | "workload"
  | "overtime"
  | "vacation"
  | "doctor"
  | "familyCare"
  | "illness"
  | "publicHoliday"
  | "paidLeave"
  | "remainingVacation";

export type ResolutionAction =
  | "confirm_overtime"
  | "correct_timesheet"
  | "adjustment"
  | "accept_exception";

export type SupportedResolutionAction = Exclude<
  ResolutionAction,
  "confirm_overtime"
>;
type LocalResolutionAction = "inspect_payslip" | "correct_extracted_worked";
export type ResolutionChoice =
  | SupportedResolutionAction
  | LocalResolutionAction;

export type CrossCheck = {
  state?: string;
  message?: string;
  timesheetValue?: number | null;
  payslipValue?: number | null;
  difference?: number | null;
  rawDifference?: number | null;
  appliedAdjustment?: number;
  accepted?: boolean;
  acceptedAt?: string;
  acceptedBy?: string;
  acceptanceReason?: string;
  resolution?: {
    actions: string[];
    recommendedAction?: string | null;
    requiresMonthReopen?: boolean;
  };
};

export type BalanceResolutionEmployee = {
  id: number;
  revision?: number;
  recalculationRequired?: boolean;
  userId: number;
  employeeName: string;
  hoursPerDay: number;
  timesheetVersionId: number | null;
  payslipRecordId: number | null;
  payslipPaidHours?: number | null;
  payslipNominalHours?: number | null;
  crossChecks?: Record<string, CrossCheck | undefined>;
};

export const checkLabels: Record<BalanceCheckKey, string> = {
  workload: "Payslip worked coverage",
  overtime: "Overtime",
  vacation: "Vacation taken",
  doctor: "Doctor",
  familyCare: "Family care",
  illness: "Illness",
  publicHoliday: "Public holidays",
  paidLeave: "Paid leave",
  remainingVacation: "Vacation remaining",
};

export const BALANCE_RESOLUTION_PRESENTATION: Record<
  ResolutionChoice,
  { label: string; summary: string; useWhen: string; consequence: string }
> = {
  inspect_payslip: {
    label: "Open payslip & values",
    summary: "Check the confirmed PDF and the values extracted from it.",
    useWhen: "Use first to verify what the source payslip actually says.",
    consequence: "Opens read-only source evidence without changing payroll.",
  },
  correct_extracted_worked: {
    label: "Correct extracted worked hours",
    summary: "Record an audited PayDay correction; the PDF stays unchanged.",
    useWhen: "Use when PayDay extracted the worked-hours value incorrectly.",
    consequence: "Records a correction and requires current balances again.",
  },
  correct_timesheet: {
    label: "Correct timesheet source",
    summary: "Fix the work-time evidence, then recalculate.",
    useWhen: "Use when the authoritative work-time record is wrong.",
    consequence:
      "Unlocks the source-correction path; recalculate after the timesheet is fixed.",
  },
  adjustment: {
    label: "Post audited adjustment",
    summary: "Post a manual correction to the audit, then recalculate.",
    useWhen: "Use when policy permits a manual balance correction.",
    consequence:
      "Changes the balance through a separately audited adjustment, then requires recalculation.",
  },
  accept_exception: {
    label: "Accept documented exception",
    summary: "Keep both sources and record why the difference is valid.",
    useWhen: "Use when the mismatch is intentional and should remain.",
    consequence:
      "Approves this mismatch without changing either source or posting an adjustment.",
  },
};

const RESOLUTION_ACTIONS = new Set<SupportedResolutionAction>([
  "correct_timesheet",
  "adjustment",
  "accept_exception",
]);

export const isResolutionAction = (
  value: string,
): value is SupportedResolutionAction =>
  RESOLUTION_ACTIONS.has(value as SupportedResolutionAction);

export const balanceResolutionOptions = (
  resolution: CrossCheck["resolution"] | undefined,
) => {
  const actions = Array.from(
    new Set((resolution?.actions ?? []).filter(isResolutionAction)),
  );
  const recommendedAction =
    resolution?.recommendedAction &&
    isResolutionAction(resolution.recommendedAction) &&
    actions.includes(resolution.recommendedAction)
      ? resolution.recommendedAction
      : null;
  return { actions, recommendedAction };
};

export const isCheckKey = (value: string): value is BalanceCheckKey =>
  value in checkLabels;

export const balanceAdjustmentUnitOptions = ["hours", "days"] as const;
export type BalanceAdjustmentUnit =
  (typeof balanceAdjustmentUnitOptions)[number];
type DisplayUnit = "hours" | "days";

export const formatValueInUnit = (
  value: number | null | undefined,
  unit: DisplayUnit,
  hoursPerDay: number,
) => {
  if (value == null) return "Not available";
  return unit === "hours"
    ? `${value.toFixed(2)} h`
    : `${(value / Math.max(hoursPerDay, 0.01)).toFixed(2)} d`;
};
